import { PropsWithChildren, ReactNode, useEffect, useState } from 'react'
import { AlmanacFileTitle } from './AlmanacFileTitle'
import { useUpdateAlmanacFile } from 'hooks/queries/almanacFiles/useUpdateAlmanacFile'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { AssetType, BaseEmoji } from 'types'
import { useKey } from 'react-use'
import { ButtonProps } from 'components/Button/Types'
import { useScheduledPostTargets } from 'hooks/queries/scheduledPostTargets/useScheduledPostTargets'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'

interface Props extends Pick<ButtonProps, 'size'> {
  id: string
  assetType?: AssetType
  onChange?: (title: string, emoji: string | null) => void
  onUpdateFinish?: () => void
  performAsyncUpdate?: boolean
  forceOpen?: boolean
  autoFocus?: boolean
  withChannels?: boolean
  isDisabled?: boolean
  trigger?: ReactNode
}

export const AlmanacFileTitleContainer = observer(
  ({
    id,
    onChange,
    children,
    trigger,
    onUpdateFinish,
    performAsyncUpdate = false,
    forceOpen = false,
    autoFocus = false,
    withChannels = false,
    size,
    isDisabled = false,
    assetType,
  }: PropsWithChildren<Props>) => {
    const rootStore = useRootStore()
    const file = rootStore.files.get(id)
    const { mutate: updateFile, mutateAsync: updateFileAsync } = useUpdateAlmanacFile({ key: id })
    const [updatedTitle, setUpdatedTitle] = useState(file?.title || '')
    const { data: scheduledPostTargets } = useScheduledPostTargets(file?.scheduled_post_id || -1)
    const { data: brandPlan } = useCurrentBrandPlan()

    // Revert the change if the user presses Escape while typing (cancels operation)
    useKey(
      'Escape',
      () => {
        setUpdatedTitle(file?.title || '')
      },
      { options: true },
      [file?.id],
    )

    useEffect(() => {
      if (file?.id) setUpdatedTitle(file.title)
    }, [file?.title]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (file) return

      const fetchFile = async () => {
        const file = await rootStore.files.fetch(id)
        setUpdatedTitle(file.title)
      }
      fetchFile()
    }, [rootStore, id, file])

    const handleChange = (title: string) => {
      let newTitle = title
      if (newTitle.length < 1) {
        newTitle = 'Untitled'
      }
      setUpdatedTitle(newTitle)
    }

    const submitChange = async () => {
      if (updatedTitle === file?.title) return

      onChange?.(updatedTitle, file?.emoji || null)
      if (performAsyncUpdate) {
        await updateFileAsync({ title: updatedTitle })
      } else {
        updateFile({ title: updatedTitle })
      }

      onUpdateFinish?.()
    }

    const handleEmojiSelect = async (emoji: BaseEmoji | null) => {
      onChange?.(updatedTitle, emoji?.native || null)
      onUpdateFinish?.()
    }

    return (
      <AlmanacFileTitle
        title={updatedTitle}
        emoji={file?.emoji || null}
        externalFileIcon={file?.external_file_icon}
        channel={file?.channel_emoji || file?.channel}
        onEmojiSelect={handleEmojiSelect}
        onChange={handleChange}
        onClose={submitChange}
        fileKey={id}
        autoFocus={autoFocus}
        forceOpen={forceOpen}
        withChannels={withChannels}
        size={size}
        isDisabled={isDisabled}
        assetType={assetType}
        trigger={trigger}
        scheduledPostTargets={scheduledPostTargets}
        brandPlan={brandPlan}
      >
        {children}
      </AlmanacFileTitle>
    )
  },
)
